import { createRegisterableComponent } from '@news-mono/web-common'
import React from 'react'
import {
    CommonwealthGamesMedalTallyWidgetColorMode,
    CommonwealthGamesMedalTallyWidget,
    CommonwealthGamesMedalTallyWidgetSize,
} from './CommonwealthGamesMedalTallyWidget'
import {
    CommonwealthGamesMedalTallyRow,
    mapMedalTallyResponse,
} from './CommonwealthGamesMedalTallyWidget.data'
import { resolveCommonwealthGamesWidgetColorMode } from '../commonwealthGamesColorMode'

const MEDALS_URL = '/medals'
const COMMONWEALTH_GAMES_START_DATE = new Date('2026-07-23T00:00:00+10:00')

export interface CommonwealthGamesMedalTallyWidgetRouteProps {
    maxRows?: number
    fullMedalTallyUrl?: string
    refreshIntervalSeconds?: number
    widgetState?: 'pre-games' | 'results'
    colorMode?: CommonwealthGamesMedalTallyWidgetColorMode
    sortable?: boolean
    size?: CommonwealthGamesMedalTallyWidgetSize
    includeTotal?: boolean
    inline?: boolean
}

export const CommonwealthGamesMedalTallyWidgetContainer: React.FC<CommonwealthGamesMedalTallyWidgetRouteProps> =
    ({
        maxRows,
        fullMedalTallyUrl,
        refreshIntervalSeconds,
        widgetState,
        colorMode,
        sortable,
        size,
        includeTotal,
        inline,
    }) => {
        const showPreGamesState =
            widgetState === 'pre-games'
                ? true
                : widgetState === 'results'
                ? false
                : new Date() < COMMONWEALTH_GAMES_START_DATE
        const effectiveColorMode =
            resolveCommonwealthGamesWidgetColorMode(colorMode)
        const [loading, setLoading] = React.useState(!showPreGamesState)
        const [error, setError] = React.useState<string | null>(null)
        const [rows, setRows] = React.useState<
            CommonwealthGamesMedalTallyRow[]
        >([])
        const [isEmptyData, setIsEmptyData] = React.useState(false)

        React.useEffect(() => {
            if (showPreGamesState) {
                setLoading(false)
                setError(null)
                setRows([])
                return
            }

            let isMounted = true
            let hasLoadedSuccessfully = false

            const load = async (isInitialLoad = false) => {
                try {
                    const response = await fetch(MEDALS_URL)
                    const text = await response.text()

                    if (!response.ok) {
                        throw new Error(
                            `Request failed with status ${response.status}`,
                        )
                    }

                    if (isMounted) {
                        const mappedRows = mapMedalTallyResponse(text)
                        setRows(mappedRows)
                        setError(null)
                        setIsEmptyData(mappedRows.length === 0)
                        hasLoadedSuccessfully = true
                    }
                } catch (e) {
                    if (
                        isMounted &&
                        (isInitialLoad || !hasLoadedSuccessfully)
                    ) {
                        setError(
                            e instanceof Error
                                ? e.message
                                : 'Failed to load medal tally data',
                        )
                    }
                } finally {
                    if (isMounted) {
                        setLoading(false)
                    }
                }
            }

            load(true)

            const refreshInterval =
                typeof refreshIntervalSeconds === 'number' &&
                refreshIntervalSeconds > 0
                    ? window.setInterval(() => {
                          void load(false)
                      }, refreshIntervalSeconds * 1000)
                    : null

            return () => {
                isMounted = false
                if (refreshInterval !== null) {
                    window.clearInterval(refreshInterval)
                }
            }
        }, [refreshIntervalSeconds, showPreGamesState])

        const showEmptyDataState = isEmptyData

        return (
            <CommonwealthGamesMedalTallyWidget
                loading={loading}
                error={error}
                rows={rows}
                showPreGamesState={showPreGamesState}
                showEmptyDataState={showEmptyDataState}
                maxRows={maxRows}
                fullMedalTallyUrl={fullMedalTallyUrl}
                colorMode={effectiveColorMode}
                sortable={sortable}
                size={size}
                includeTotal={includeTotal}
                inline={inline}
            />
        )
    }

export const CommonwealthGamesMedalTallyWidgetRegistration =
    createRegisterableComponent(
        'commonwealth-games-medal-tally-widget',
        (props: CommonwealthGamesMedalTallyWidgetRouteProps) => (
            <CommonwealthGamesMedalTallyWidgetContainer {...props} />
        ),
    )
